import { ref } from 'vue'
import type { ViteAppConfiguration } from '../types/config'

/**
 * Composable for WooCommerce cart integration
 * Handles adding domain products to cart
 */
export function useCart() {
  const addingDomain = ref<string | null>(null)
  const cartError = ref<string | null>(null)
  const cartSuccess = ref<string | null>(null)

  /**
   * Add domain to WooCommerce cart
   *
   * @param domain - Full domain name (e.g., 'example.co.za')
   * @param price - Annual registration price
   * @param years - Number of years to register (default: 1)
   */
  const addToCart = async (domain: string, price: number, years: number = 1) => {
    addingDomain.value = domain
    cartError.value = null
    cartSuccess.value = null

    // DEBUG: Log what we're about to send
    console.log('[useCart] Adding to cart:', { domain, price, years, priceType: typeof price })

    try {
      // Get WordPress configuration from global
      const config = (window as any).conflatedDomainsConfig as ViteAppConfiguration

      if (!config || !config.restUrl || !config.nonce) {
        throw new Error('WordPress REST API configuration not found')
      }

      const payload = {
        domain,
        price,
        years
      }

      // DEBUG: Log the payload before stringify
      console.log('[useCart] Payload before stringify:', payload)
      console.log('[useCart] Payload after stringify:', JSON.stringify(payload))

      // Call WordPress REST API to add domain to cart
      const response = await fetch(`${config.restUrl}/${config.apiNamespace}/cart/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': config.nonce
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json()

      if (!response.ok) {
        // Extract error message from response
        const errorMessage = data.message || data.data?.message || response.statusText
        throw new Error(errorMessage)
      }

      // WordPress REST API wraps response in {success: true, data: {...}}
      const responseData = data.data || data

      // Check if the operation was successful
      if (data.success === false || !responseData.cart_url) {
        throw new Error(data.message || 'Failed to add domain to cart')
      }

      // Check redirect setting
      if (config.redirectToCart) {
        // Redirect to cart page after a short delay to show loading state
        setTimeout(() => {
          window.location.href = responseData.cart_url
        }, 500)
      } else {
        // Show success notification and stay on page
        cartSuccess.value = data.message || `${domain} has been added to your cart.`

        // Clear success message after 5 seconds
        setTimeout(() => {
          cartSuccess.value = null
        }, 5000)
      }

    } catch (e) {
      cartError.value = e instanceof Error ? e.message : 'Failed to add domain to cart'
      console.error('Add to cart error:', e)
    } finally {
      addingDomain.value = null
    }
  }

  return {
    addingDomain,
    cartError,
    cartSuccess,
    addToCart
  }
}
